import React, {
  useImperativeHandle,
  useEffect,
  forwardRef,
  useState,
  useCallback,
  useRef,
} from 'react';
import { Form, Input, Checkbox, Table, message } from 'antd';
import { useHistory } from 'react-router-dom';
import { deepCopy } from '@utils/common.js';
import { isNumber } from '@utils/currency.js';
import { EditTableByRow } from '@components';
import alert from '@components/alert';

import { tableColumns, validateEmailAPI } from './client.data.jsx';
import BulkAddUsersModal from './components/BulkAddUsersModal';
import BulkAddUsersButton from './components/BulkAddUsersButton';
import './components/BulkAddUsersModal.scss';
import {
  CLIENT_TEAM_BULK_ADD_HELPER,
  getBulkAddSuccessMessage,
} from './utils/bulkAddUsers.ui';
import { EMAIL_FORMAT_REGEX } from './utils/parseBulkEmails';

const { TextArea } = Input;

const Client = ({
  cRef,
  clientFormData,
  groupData,
  getGroupData,
  groupManip,
  createStep = false,
  renderAction,
  setMemberOrClientChange = () => {},
  projectName = '',
  projectId = '',
}) => {
  const history = useHistory();
  const [clientForm] = Form.useForm();
  const [inputValue, setInputValue] = useState();
  const isSettingPage = history.location.pathname.includes('setting');
  const [editInfo, setEditInfo] = useState(null);
  const [sendEmail, setSendEmail] = useState('yes');
  const [bulkAddVisible, setBulkAddVisible] = useState(false);

  // Ref for EditTableByRow
  const editTableRef = useRef(null);

  // Ref for debounced email validation timers (per row index)
  const emailValidationTimersRef = useRef({});

  // State for email validation loading
  const [validatingEmailIndex, setValidatingEmailIndex] = useState(null);

  // Track which row indices have passed domain check (Save enabled only when true for current row)
  const [emailValidatedForRow, setEmailValidatedForRow] = useState({});

  const layout = {
    labelAlign: 'left',
    colon: false,
  };

  const StatusComp = forwardRef(({ value }) => {
    if (!value) {
      return null;
    }
    return <div className={`status ${value}`}>{value}</div>;
  });

  const getNewEditInfo = (data, index) => ({
    ...data,
    index,
    fieldKey: index,
    key: index,
    email: '',
    isEUBased: null,
    role_name: '',
    name: '',
  });

  const cleanEmptyEntries = () => {
    const currentList = clientForm.getFieldsValue().clientList || [];
    const cleanedList = currentList.filter(
      (item) => item && item.email && item.email.trim() !== '',
    );

    if (cleanedList.length !== currentList.length) {
      clientForm.setFieldsValue({ clientList: cleanedList });
    }

    return cleanedList;
  };

  const handleAdd = () => {
    cleanEmptyEntries();

    const newClientList = deepCopy(clientForm.getFieldsValue().clientList);
    const newItem = getNewEditInfo({ isNew: true }, newClientList?.length || 0);
    if (!newClientList) {
      return;
    }
    newClientList[newClientList?.length] = newItem;
    clientForm.setFieldsValue({ clientList: newClientList });
    setEditInfo(newItem);

    setTimeout(() => {
      const tableBody = document.querySelector('.ant-table-tbody');
      if (tableBody) {
        tableBody.scrollTop = tableBody.scrollHeight;
      }

      const tableContainer = document.querySelector('.ant-table-body');
      if (tableContainer) {
        tableContainer.scrollTop = tableContainer.scrollHeight;
      }
    }, 100);

    return newItem;
  };

  const handleRemove = (remove, index) => () => {
    if (!createStep) {
      groupManip.deleteClient(clientForm.getFieldsValue().clientList[index]);
    }
    remove(index);
    setMemberOrClientChange('edit');
  };

  const cancelEditing = () => {
    if (editInfo?.index != null) {
      setEmailValidatedForRow((prev) => {
        const next = { ...prev };
        delete next[editInfo.index];
        return next;
      });
    }
    if (editInfo?.isNew) {
      const currentList = clientForm.getFieldsValue().clientList || [];
      const filteredList = currentList.filter(
        (_, idx) => idx !== editInfo.index,
      );
      clientForm.setFieldsValue({ clientList: filteredList });
      setEditInfo(null);
    } else if (isNumber(editInfo?.index)) {
      const newClientList = deepCopy(clientForm.getFieldValue('clientList'));
      if (editInfo.email && editInfo.email.trim() !== '') {
        newClientList[editInfo.index] = editInfo;
        clientForm.setFieldsValue({ clientList: newClientList });
      } else {
        const filteredList = newClientList.filter(
          (_, idx) => idx !== editInfo.index,
        );
        clientForm.setFieldsValue({ clientList: filteredList });
      }
      setEditInfo(null);
    }

    setTimeout(() => cleanEmptyEntries(), 0);
  };

  useImperativeHandle(cRef, () => ({
    getClientForm: () => clientForm,
    cancelEditing,
    getEditInfo: () => editInfo,
  }));

  const handleInputFocus = (_index) => {
    const data = clientForm.getFieldsValue().clientList;
    setInputValue(data[_index]);
  };

  const handleInputBlur = (_index) => {
    if (createStep) {
      return;
    }
    const data = clientForm.getFieldsValue().clientList;
    if (
      (inputValue.email && !data[_index].email) ||
      inputValue.email !== data[_index].email
    ) {
      groupManip.deleteClient({ ...inputValue });
    }
    if (data[_index].email) {
      groupManip.updateClient({ ...data[_index] });
    }
  };

  const newHandleInputBlur = (newData) => {
    if (createStep) {
      return;
    }
    if (newData.email) {
      groupManip.updateClient({ ...newData });
      const newClientList = deepCopy(clientForm.getFieldsValue().clientList);

      if (!newClientList) {
        return;
      }
      newClientList[newData.index] = newData;
      clientForm.setFieldsValue({ clientList: newClientList });
    }
  };

  // Helper function to clear email field - NOW USES THE REF
  const clearEmailField = useCallback(
    (index) => {
      console.log('Clearing email field for index:', index);

      // Method 1: Clear via EditTableByRow ref (this is the key fix!)
      if (editTableRef.current) {
        editTableRef.current.clearField('email', '');
        console.log('Cleared via editTableRef');
      }

      // Method 2: Also update the parent form data
      const currentClientList = clientForm.getFieldsValue().clientList || [];
      const newClientList = deepCopy(currentClientList);

      if (newClientList[index]) {
        newClientList[index] = {
          ...newClientList[index],
          email: '',
        };

        clientForm.setFieldsValue({ clientList: newClientList });
      }

      // Update editInfo
      if (editInfo && editInfo.index === index) {
        setEditInfo({
          ...editInfo,
          email: '',
        });
      }
    },
    [clientForm, editInfo],
  );

  // Email validation handler
  const handleEmailValidation = useCallback(
    async (rowData, index) => {
      const emailValue = typeof rowData === 'object' ? rowData?.email : rowData;
      const email = typeof emailValue === 'string' ? emailValue.trim() : '';

      console.log('handleEmailValidation called with:', { email, index });

      if (!email) {
        return;
      }

      if (!EMAIL_FORMAT_REGEX.test(email)) {
        return;
      }

      // if (email.includes('@pwc.com')) {
      //   message.error('Correct email is required!');
      //   clearEmailField(index);
      //   return;
      // }

      const currentClientList = clientForm.getFieldsValue().clientList || [];
      const duplicateEmail = currentClientList.some(
        (client, idx) => client?.email === email && idx !== index,
      );

      if (duplicateEmail) {
        message.error('Email has already been added.');
        clearEmailField(index);
        return;
      }

      try {
        setValidatingEmailIndex(index);

        const response = await validateEmailAPI(email);
        console.log('API Response:', response);
        if (response?.sendEmail === 'no') setSendEmail(response?.sendEmail);

        if (response?.isPresent === true) {
          console.log('Email is valid, keeping value');
          setEmailValidatedForRow((prev) => ({ ...prev, [index]: true }));

          const newClientList = deepCopy(
            clientForm.getFieldsValue().clientList,
          );

          if (newClientList[index]) {
            const sendEmailValue =
              response?.sendEmail != null && String(response.sendEmail) === 'no'
                ? 'no'
                : 'yes';
            newClientList[index] = {
              ...newClientList[index],
              email,
              sendEmail: sendEmailValue,
            };

            clientForm.setFieldsValue({ clientList: newClientList });
            newHandleInputBlur({ ...newClientList[index], index });
          }
        } else {
          console.log('Email is INVALID, clearing field');
          setEmailValidatedForRow((prev) => {
            const next = { ...prev };
            delete next[index];
            return next;
          });

          // Show error message
          message.error(
            response?.description ||
              'Domain is not present. Contact integration team to onboard email domain and configure authentication pattern.',
          );

          // Clear the email field
          clearEmailField(index);
        }
      } catch (error) {
        console.error('Email validation failed:', error);

        const errorResponse = error?.response?.data || error;

        if (errorResponse?.isPresent === false) {
          setEmailValidatedForRow((prev) => {
            const next = { ...prev };
            delete next[index];
            return next;
          });
          message.error(
            errorResponse?.description ||
              'Domain is not present. Contact integration team to onboard email domain and configure authentication pattern.',
          );
          clearEmailField(index);
        } else {
          message.error('Failed to validate email. Please try again.');
        }
      } finally {
        setValidatingEmailIndex(null);
      }
    },
    [clientForm, newHandleInputBlur, clearEmailField],
  );

  const EMAIL_DEBOUNCE_MS = 500;

  // Debounced email change: validate regex, then after idle call domain check API
  const handleEmailChange = useCallback(
    (rowData, index) => {
      const emailValue = typeof rowData === 'object' ? rowData?.email : rowData;
      const email = typeof emailValue === 'string' ? emailValue.trim() : '';

      if (!email) {
        if (emailValidationTimersRef.current[index] != null) {
          clearTimeout(emailValidationTimersRef.current[index]);
          delete emailValidationTimersRef.current[index];
        }
        setEmailValidatedForRow((prev) => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
        return;
      }

      if (!EMAIL_FORMAT_REGEX.test(email)) {
        if (emailValidationTimersRef.current[index] != null) {
          clearTimeout(emailValidationTimersRef.current[index]);
          delete emailValidationTimersRef.current[index];
        }
        setEmailValidatedForRow((prev) => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
        return;
      }

      setEmailValidatedForRow((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });

      if (emailValidationTimersRef.current[index] != null) {
        clearTimeout(emailValidationTimersRef.current[index]);
      }

      emailValidationTimersRef.current[index] = setTimeout(() => {
        delete emailValidationTimersRef.current[index];
        const currentList = clientForm.getFieldsValue().clientList || [];
        const rowDataWithEmail = currentList[index]
          ? { ...currentList[index], email }
          : { email, index };
        handleEmailValidation(rowDataWithEmail, index);
      }, EMAIL_DEBOUNCE_MS);
    },
    [clientForm, handleEmailValidation],
  );

  const matchGroups = (_index) => {
    const _email = clientForm.getFieldsValue().clientList[_index]?.email;
    const groupRelated = createStep ? groupData : getGroupData();

    return groupRelated
      ?.map((group) => {
        const _groupMembers = group.groupMembers.filter(
          (currentValue) => currentValue.client?.email === _email,
        );
        return _groupMembers.length > 0 ? group.groupInfo.groupName : null;
      })
      .filter((value) => value != null);
  };

  useEffect(() => {
    const clientList = clientFormData?.clientList || [];
    clientForm.setFieldsValue({ clientList });
  }, [clientFormData]);

  useEffect(() => {
    return () => {
      Object.values(emailValidationTimersRef.current).forEach(clearTimeout);
      emailValidationTimersRef.current = {};
    };
  }, []);

  // When editing an existing user (not new), treat email as already validated so Save is enabled for role-only changes
  useEffect(() => {
    if (editInfo != null && editInfo.index != null && !editInfo.isNew) {
      setEmailValidatedForRow((prev) => ({ ...prev, [editInfo.index]: true }));
    }
  }, [editInfo]);

  const saveData = async (newData, index) => {
    try {
      if (!newData.email || newData.email.trim() === '') {
        message.error('Email is required');
        return;
      }

      if (!EMAIL_FORMAT_REGEX.test(newData.email)) {
        message.error('Please enter a valid email address');
        return;
      }

      // if (newData.email.includes('@pwc.com')) {
      //   message.error('Correct email is required!');
      //   return;
      // }

      const currentClientList = clientForm.getFieldsValue().clientList || [];
      const duplicateEmail = currentClientList.some(
        (client, idx) => client?.email === newData.email && idx !== index,
      );

      if (duplicateEmail) {
        message.error('Email has already been added.');
        return;
      }

      let newClientList = deepCopy(currentClientList);

      if (editInfo?.isNew) {
        const actualIndex = newClientList.findIndex(
          (item) => item.key === editInfo.key || item.index === editInfo.index,
        );
        if (actualIndex !== -1) {
          newClientList[actualIndex] = {
            ...newClientList[actualIndex],
            ...newData,
            sendEmail:
              newData.sendEmail ??
              newClientList[actualIndex].sendEmail ??
              sendEmail,
          };
        }
      } else {
        newClientList[index] = { ...newClientList[index], ...newData };
      }

      clientForm.setFieldsValue({ clientList: newClientList });

      if (!createStep && newData.email) {
        groupManip.updateClient({ ...newData });
      }

      setMemberOrClientChange(editInfo?.isNew ? 'add' : 'edit');
      setEditInfo(null);

      setTimeout(() => cleanEmptyEntries(), 0);
    } catch (err) {
      console.log('Save error: ', err);
      message.error('Failed to save client data');
    }
  };

  const deleteRow = (record, remove) => {
    const index = record.index;
    alert.confirm({
      content: (
        <>
          Are you sure you want to remove {record?.fullName}?
          <br />
          {matchGroups(index).map((group) => (
            <React.Fragment key={group}>
              · {group}
              <br />
            </React.Fragment>
          ))}
        </>
      ),
      okText: 'Remove',
      onOk: handleRemove(remove, index),
    });
  };

  const isNoData = () => {
    let isNoData = false;
    if ((clientForm.getFieldValue('clientList') || []).length === 0) {
      isNoData = true;
    } else {
      if (
        clientForm.getFieldValue('clientList').length === 1 &&
        clientForm.getFieldValue('clientList')[0].isNew &&
        editInfo
      ) {
        isNoData = true;
      }
    }
    return isNoData;
  };

  const copyAllEmails = () => {
    message.destroy();
    navigator.clipboard.writeText(
      (clientForm.getFieldValue('clientList') || [])
        ?.filter((i) => i.email)
        .map((i) => i.email)
        .join(';'),
    );
    message.success('Email addresses copied!');
  };

  const getFilteredClientList = () => {
    const clientList = clientForm.getFieldValue('clientList') || [];
    return clientList.filter(
      (item) =>
        (item && item.email && item.email.trim() !== '') ||
        editInfo?.index === item.index,
    );
  };

  const isEditing = (record) =>
    editInfo &&
    (editInfo.index === record.index ||
      editInfo.key === record.key ||
      (editInfo.email && editInfo.email === record.email));

  const handleBulkAddConfirm = (validRows) => {
    cleanEmptyEntries();
    const currentList = deepCopy(clientForm.getFieldsValue().clientList || []);
    const filteredList = currentList.filter(
      (item) => item && item.email && item.email.trim() !== '',
    );

    const newClients = validRows.map((row, i) => {
      const idx = filteredList.length + i;
      return {
        ...row.memberData,
        name: '',
        index: idx,
        fieldKey: idx,
        key: idx,
      };
    });

    const updatedList = [...filteredList, ...newClients];
    clientForm.setFieldsValue({ clientList: updatedList });

    if (!createStep) {
      newClients.forEach((client) => {
        groupManip.updateClient({ ...client });
      });
    }

    setMemberOrClientChange('add');
    message.success(getBulkAddSuccessMessage(newClients.length, createStep));
  };

  const bulkAddButton = (
    <BulkAddUsersButton
      disabled={Boolean(editInfo)}
      onClick={() => setBulkAddVisible(true)}
    />
  );

  return (
    <>
      <div
        className={`pwc-team-client ${
          isSettingPage ? 'client-setting-wrapper' : ''
        }`}
      >
        {(isSettingPage || createStep) && (
          <div className="createPrompt1">{CLIENT_TEAM_BULK_ADD_HELPER}</div>
        )}
        <Form
          form={clientForm}
          name="createProject"
          autoComplete="off"
          {...layout}
        >
          <Form.List name="clientList" {...layout} className="form-list">
            {(fields, { add, remove }) => (
              <div>
                <EditTableByRow
                  ref={editTableRef}
                  dataKey="index"
                  tableProps={{
                    columns: tableColumns({
                      editInfo,
                      setEditInfo,
                      data: clientForm.getFieldValue('clientList'),
                      handleRemove,
                      remove,
                      matchGroups,
                      copyAllEmails,
                      isSettingPage,
                      handleInputBlur: newHandleInputBlur,
                      isEditing,
                      onEmailValidation: handleEmailValidation,
                      onEmailChange: handleEmailChange,
                      validatingEmailIndex,
                      form: clientForm,
                      projectName,
                      projectId,
                    }),
                    dataSource: getFilteredClientList().map((item, index) => ({
                      ...item,
                      index,
                    })),
                    pagination: false,
                    scroll: {
                      y: window.screen.height - 500,
                    },
                    locale: {
                      emptyText: () => <div style={{ visibility: 'hidden' }} />,
                    },
                  }}
                  showAddNewBtn={!editInfo}
                  setEditInfo={setEditInfo}
                  handleAdd={handleAdd}
                  deleteRow={(record) => deleteRow(record, remove)}
                  saveRow={saveData}
                  cancelRow={cancelEditing}
                  disableDeletionOfFirstItem={false}
                  validatingEmailIndex={validatingEmailIndex}
                  emailValidatedForRow={emailValidatedForRow}
                  saveRequiresEmailValidation={true}
                  extraActions={bulkAddButton}
                />
              </div>
            )}
          </Form.List>
        </Form>
      </div>

      <BulkAddUsersModal
        visible={bulkAddVisible}
        teamType="client"
        createStep={createStep}
        existingUsers={clientForm.getFieldValue('clientList') || []}
        onConfirm={handleBulkAddConfirm}
        onCancel={() => setBulkAddVisible(false)}
      />

      {renderAction && renderAction()}
    </>
  );
};

export default Client;
